"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const MandalsContext = createContext();
export const useMandals = () => useContext(MandalsContext);

export const MandalsProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [mandals, setMandals] = useState([]);
  const [mandalsLoading, setMandalsLoading] = useState(false);

  const mountedRef = useRef(true);

  const MANDALS_ENDPOINT = "/mandals";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetMandals = (value) => {
    if (mountedRef.current) setMandals(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchMandals = useCallback(
    async (district_id = "") => {
      if (!token) return;

      setMandalsLoading(true);

      try {
        let url = `${baseURL}${MANDALS_ENDPOINT}`;

        if (district_id) {
          url += `?district_id=${district_id}`;
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Fetch failed");

        safeSetMandals(payload?.data || []);

        return payload;
      } catch (err) {
        console.error("[Mandals] fetch error:", err);

        if (err.message?.toLowerCase().includes("token")) {
          logout();
        }

        safeSetMandals([]);
      } finally {
        if (mountedRef.current) setMandalsLoading(false);
      }
    },
    [token, baseURL, logout]
  );

  /* ---------------- CREATE ---------------- */
  const createMandal = useCallback(
    async (data) => {
      try {
        const res = await fetch(`${baseURL}${MANDALS_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchMandals(data.district_id);
        return payload;
      } catch (err) {
        console.error("[Mandals] create error:", err);
        throw err;
      }
    },
    [token, adminId, fetchMandals]
  );

  /* ---------------- UPDATE ---------------- */
  const updateMandal = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(`${baseURL}${MANDALS_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchMandals(data.district_id);
        return payload;
      } catch (err) {
        console.error("[Mandals] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchMandals]
  );

  /* ---------------- DELETE ---------------- */
  const deleteMandal = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${MANDALS_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetMandals((prev) =>
          prev.filter((m) => String(m.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Mandals] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateMandalStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(
          `${baseURL}${MANDALS_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              status,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Status failed");

        safeSetMandals((prev) =>
          prev.map((m) =>
            String(m.id) === String(id)
              ? { ...m, status }
              : m
          )
        );

        return payload;
      } catch (err) {
        console.error("[Mandals] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchMandals().catch(() => {});
  }, [isReady, token, fetchMandals]);

  return (
    <MandalsContext.Provider
      value={{
        mandals,
        mandalsLoading,
        fetchMandals,
        createMandal,
        updateMandal,
        deleteMandal,
        updateMandalStatus,
      }}
    >
      {children}
    </MandalsContext.Provider>
  );
};